import type * as React from 'react';

import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { Text } from '../../components/ui/text';
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuGroup,
  MenuGroupLabel,
  MenuSeparator,
  MenuItem,
} from '../../components/ui/menu';

import type { DataTableGroupActionsContext } from './data-table-types';
import type { Omittable } from "../../lib/props";

/**
 * Default group actions dropdown shared across workspace tables.
 *
 * Renders a menu with: Collapse group, Select all, Expand/Collapse all groups.
 * Callers can append surface-specific items (e.g. "Automate stage") via
 * `append` — they render in their own section below the shared items.
 */
export function renderDefaultGroupActions<TData>({
  isCollapsed,
  isGroupSelected,
  collapseGroup,
  collapseAll,
  expandAll,
  allCollapsed,
  selectAll,
  deselectAll,
  append,
}: DataTableGroupActionsContext<TData> & Omittable<{
  append?: React.ReactNode;
}>): React.ReactNode {
  return (
    <Menu>
      <MenuTrigger render={<Button variant="ghost" size="icon-xs" />}>
        <Icon name="more-horizontal" />
      </MenuTrigger>
      <MenuPopup align="start">
        {/* MenuGroupLabel resolves its labelled set through MenuGroup context
            and throws without one, so the heading and the actions it names are
            one group. `append` stays outside — it is the caller's own set. */}
        <MenuGroup>
          <MenuGroupLabel>Group options</MenuGroupLabel>
          <MenuSeparator />
          <MenuItem onClick={collapseGroup}>
            <Icon name={isCollapsed ? 'chevrons-down' : 'chevrons-up'} size="lg" color="muted" />
            <Text size="sm">{isCollapsed ? 'Expand group' : 'Collapse group'}</Text>
          </MenuItem>
          <MenuSeparator />
          <MenuItem onClick={isGroupSelected ? deselectAll : selectAll}>
            <Icon name="list-checks" size="lg" color="muted" />
            <Text size="sm">{isGroupSelected ? 'Deselect all' : 'Select all'}</Text>
          </MenuItem>
          <MenuItem onClick={allCollapsed ? expandAll : collapseAll}>
            <Icon
              name={allCollapsed ? 'chevrons-down' : 'chevrons-up'}
              size="lg"
              color="muted"
            />
            <Text size="sm">
              {allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
            </Text>
          </MenuItem>
        </MenuGroup>
        {append ? (
          <>
            <MenuSeparator />
            {append}
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}
